<script lang="ts">
  // Admin overlay: manage users and the camera catalog. Camera edits take
  // effect in go2rtc immediately (the backend pushes them on save).
  import { createEventDispatcher, onMount } from "svelte";
  import {
    adminListUsers, adminCreateUser, adminResetPassword, adminDeleteUser,
    adminListCameras, adminCreateCamera, adminUpdateCamera, adminDeleteCamera,
    type AdminUser, type Camera,
  } from "./api";

  const dispatch = createEventDispatcher<{ close: void }>();

  let users: AdminUser[] = [];
  let cameras: Camera[] = [];
  let error = "";

  // new-user form
  let nu = "", np = "", nAdmin = false;
  // new/edit-camera form
  let cName = "", cUrl = "", cTranscode = false, cMirror = false, editingId: number | null = null;
  // Mirror is a transcode-time filter — it only applies when transcoding.
  $: if (!cTranscode) cMirror = false;

  async function refresh(): Promise<void> {
    error = "";
    try {
      [users, cameras] = await Promise.all([adminListUsers(), adminListCameras()]);
    } catch (e) { error = String(e).replace(/^Error:\s*/, ""); }
  }
  onMount(refresh);

  function fail(e: unknown): void { error = String(e).replace(/^Error:\s*/, ""); }

  // --- users ---
  async function addUser(): Promise<void> {
    try { await adminCreateUser(nu.trim(), np, nAdmin); nu = ""; np = ""; nAdmin = false; await refresh(); }
    catch (e) { fail(e); }
  }
  async function resetPw(u: string): Promise<void> {
    const pw = prompt(`New password for "${u}" (min 6 chars):`);
    if (!pw) return;
    try { await adminResetPassword(u, pw); } catch (e) { fail(e); }
  }
  async function delUser(u: string): Promise<void> {
    if (!confirm(`Delete user "${u}"?`)) return;
    try { await adminDeleteUser(u); await refresh(); } catch (e) { fail(e); }
  }

  // --- cameras ---
  function editCam(c: Camera): void {
    editingId = c.id; cName = c.name; cUrl = c.url ?? "";
    cTranscode = !!c.transcode; cMirror = !!c.mirror;
  }
  function clearCamForm(): void {
    editingId = null; cName = ""; cUrl = ""; cTranscode = false; cMirror = false;
  }
  async function saveCam(): Promise<void> {
    try {
      if (editingId == null) await adminCreateCamera(cName.trim(), cUrl.trim(), cTranscode, cMirror);
      else await adminUpdateCamera(editingId, cName.trim(), cUrl.trim(), cTranscode, cMirror);
      clearCamForm();
      await refresh();
    } catch (e) { fail(e); }
  }
  async function delCam(c: Camera): Promise<void> {
    if (!confirm(`Delete camera "${c.name}"?`)) return;
    try { await adminDeleteCamera(c.id); if (editingId === c.id) clearCamForm(); await refresh(); }
    catch (e) { fail(e); }
  }
</script>

<div class="modal-mask" on:click|self={() => dispatch("close")}>
  <div class="modal">
    <div class="modal-head">
      <h2>Admin</h2>
      <button on:click={() => dispatch("close")}>Close</button>
    </div>

    {#if error}<div class="modal-error">{error}</div>{/if}

    <div class="admin-cols">
      <!-- Cameras -->
      <section class="group">
        <h3>Cameras</h3>
        <table class="tbl">
          <thead><tr><th>Name</th><th>RTSP URL</th><th>H.264</th><th></th></tr></thead>
          <tbody>
            {#each cameras as c}
              <tr>
                <td>{c.name}</td>
                <td class="mono url">{c.url}</td>
                <td>{c.transcode ? (c.mirror ? "transcode · mirror" : "transcode") : "passthrough"}</td>
                <td class="actions">
                  <button on:click={() => editCam(c)}>Edit</button>
                  <button class="danger" on:click={() => delCam(c)}>Delete</button>
                </td>
              </tr>
            {/each}
            {#if cameras.length === 0}<tr><td colspan="4" class="muted">No cameras yet.</td></tr>{/if}
          </tbody>
        </table>

        <div class="form">
          <h4>{editingId == null ? "Add camera" : "Edit camera"}</h4>
          <label>Name<input bind:value={cName} placeholder="Front gate" /></label>
          <label>RTSP URL<input bind:value={cUrl} placeholder="rtsp://10.8.0.149:8554/cam0" /></label>
          <label class="chk">
            <input type="checkbox" bind:checked={cTranscode} />
            Transcode to 8-bit H.264 (for H.265 / 10-bit “lidar” streams &amp; Firefox)
          </label>
          <label class="chk" class:muted={!cTranscode}>
            <input type="checkbox" bind:checked={cMirror} disabled={!cTranscode} />
            Mirror left-right (requires transcode)
          </label>
          <div class="row">
            <button class="primary" on:click={saveCam} disabled={!cName.trim() || !cUrl.trim()}>
              {editingId == null ? "Add" : "Save"}
            </button>
            {#if editingId != null}<button on:click={clearCamForm}>Cancel</button>{/if}
          </div>
        </div>
      </section>

      <!-- Users -->
      <section class="group">
        <h3>Users</h3>
        <table class="tbl">
          <thead><tr><th>Username</th><th>Role</th><th></th></tr></thead>
          <tbody>
            {#each users as u}
              <tr>
                <td>{u.username}</td>
                <td>{u.is_admin ? "admin" : "user"}</td>
                <td class="actions">
                  <button on:click={() => resetPw(u.username)}>Reset pw</button>
                  {#if !u.is_admin}<button class="danger" on:click={() => delUser(u.username)}>Delete</button>{/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>

        <div class="form">
          <h4>Add user</h4>
          <label>Username<input bind:value={nu} /></label>
          <label>Password<input type="password" bind:value={np} placeholder="min 6 chars" /></label>
          <label class="chk"><input type="checkbox" bind:checked={nAdmin} /> Administrator</label>
          <button class="primary" on:click={addUser} disabled={!nu.trim() || np.length < 6}>Add</button>
        </div>
      </section>
    </div>
  </div>
</div>
